/**
 * Two-step "change email" modal.
 *
 *   step 1 — "edit": new-email form. Confirm advances to step 2.
 *   step 2 — "confirm": before/after summary; Yes calls the mutation.
 *
 * Both steps live inside a single <dialog> so the transition between
 * editing and confirming is seamless (no stacked dialogs, no flicker).
 */

import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "../../api/client";
import { useUpdateProfile } from "../../api/account";
import Modal from "../../components/Modal";

interface ChangeEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentEmail: string;
}

export default function ChangeEmailModal({
  isOpen,
  onClose,
  currentEmail,
}: ChangeEmailModalProps) {
  const update = useUpdateProfile();
  const [step, setStep] = useState<"edit" | "confirm">("edit");
  const [next, setNext] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Reset whenever the modal is closed so re-opening starts fresh.
  useEffect(() => {
    if (!isOpen) {
      setStep("edit");
      setNext("");
      setError(null);
    }
  }, [isOpen]);

  const trimmed = next.trim();
  const dirty =
    trimmed.length > 0 &&
    trimmed.toLowerCase() !== (currentEmail ?? "").toLowerCase();

  function handleEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!dirty) return;
    setStep("confirm");
  }

  async function handleConfirm() {
    setError(null);
    try {
      await update.mutateAsync({ email: trimmed });
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not change email.",
      );
      setStep("edit");
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={step === "edit" ? "Change email" : "Confirm new email"}
    >
      {step === "edit" ? (
        <form className="account-form" onSubmit={handleEditSubmit}>
          <label className="form-row">
            <span className="form-row__label">Current email</span>
            <input
              type="email"
              value={currentEmail}
              readOnly
              className="form-row__input form-row__input--readonly"
            />
          </label>
          <label className="form-row">
            <span className="form-row__label">New email</span>
            <input
              type="email"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="form-row__input"
              required
              autoFocus
            />
          </label>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="form-actions form-actions--inline">
            <button
              type="button"
              className="button button--ghost"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={!dirty}
            >
              Confirm
            </button>
          </div>
        </form>
      ) : (
        <div className="account-form">
          <p className="section-help">
            You'll use the new address to sign in next time. Make sure you
            still have access to it.
          </p>
          <dl className="diff-list">
            <div className="diff-list__row">
              <dt className="diff-list__term">Before</dt>
              <dd className="diff-list__desc">{currentEmail || "—"}</dd>
            </div>
            <div className="diff-list__row diff-list__row--after">
              <dt className="diff-list__term">After</dt>
              <dd className="diff-list__desc">{trimmed}</dd>
            </div>
          </dl>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="form-actions form-actions--inline">
            <button
              type="button"
              className="button button--ghost"
              onClick={() => setStep("edit")}
              disabled={update.isPending}
            >
              Back
            </button>
            <button
              type="button"
              className="button button--primary"
              onClick={handleConfirm}
              disabled={update.isPending}
            >
              {update.isPending ? "Saving…" : "Yes, change it"}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
